/**
 * Plain-English copy for creating and managing bills: confirmations that
 * state the money consequence, toasts that name who and how much, readable
 * dates and target summaries.
 *
 * Most bursars and admins are not technical, so every message here says who
 * is affected, how much they owe and whether the action can be undone.
 */
import { formatCurrency } from '@/utils/currency';
import { billableTypeName, normalizeCode } from '@/utils/billingLabels';
import { billableTypeLabel, isFormSubmissionBill } from '@/utils/billableLabel';
import type { BillFieldsFragment } from '@/types/operations';
import type { Bill, BillAggregates } from '@/types/composition-functions';

/** "1 student", "3 students", "2 classes". */
export function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * One readable date format everywhere: "6 Oct 2026".
 *
 * Date-only values ("2026-10-31") are read as the local calendar day — parsing
 * them with `new Date()` would treat them as UTC midnight. Lighthouse
 * DateTime values ("2026-10-06 09:12:33") are read as local time.
 */
export function formatReadableDate(value: string | Date | null | undefined): string {
  if (!value) return '';

  let date: Date;
  if (value instanceof Date) {
    date = value;
  } else {
    const dateOnly = DATE_ONLY.exec(value.trim());
    date = dateOnly
      ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
      : new Date(value.trim().replace(' ', 'T'));
  }

  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Today at local midnight, for date pickers that must not offer past days. */
export function startOfToday(now: Date = new Date()): Date {
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

// ---------------------------------------------------------------------------
// Who a bill is for
// ---------------------------------------------------------------------------

const TARGET_NOUNS: Record<string, [string, string]> = {
  student: ['student', 'students'],
  classroom: ['class', 'classes'],
  classroomsection: ['class section', 'class sections'],
  programme: ['programme', 'programmes'],
};

/** "2 classes", "1 student" for the bill-creation target type. */
export function targetCount(billableType: string, count: number): string {
  const [singular, plural] = TARGET_NOUNS[billableType.toLowerCase()] ?? ['group', 'groups'];
  return pluralize(count, singular, plural);
}

/** "JSS 1A, JSS 1B and 3 more" — keeps long selections readable. */
export function listNames(names: string[], max = 4): string {
  const clean = names.filter(Boolean);
  if (clean.length <= max) {
    if (clean.length <= 1) return clean[0] ?? '';
    return `${clean.slice(0, -1).join(', ')} and ${clean[clean.length - 1]}`;
  }
  return `${clean.slice(0, max).join(', ')} and ${clean.length - max} more`;
}

/** "2 class sections: JSS 1A and JSS 1B", or just the names when they are students. */
export function describeTargets(billableType: string, names: string[]): string {
  if (!names.length) return targetCount(billableType, 0);
  const label = listNames(names);
  if (billableType.toLowerCase() === 'student' && names.length <= 4) return label;
  return `${targetCount(billableType, names.length)}: ${label}`;
}

/**
 * The kind of payer under the name: "Whole class", "Class section",
 * "Individual student", or the form's title for an application fee.
 */
export function billedToType(bill: Pick<BillFieldsFragment, 'billable_type' | 'billable'>): string {
  return isFormSubmissionBill(bill)
    ? billableTypeLabel(bill as BillFieldsFragment)
    : billableTypeName(bill.billable_type);
}

/** True when one student owes the bill directly (not a class, section or programme). */
export function isStudentBill(bill: { billable_type: string }): boolean {
  return normalizeCode(bill.billable_type) === 'student';
}

// ---------------------------------------------------------------------------
// Publish / cancel an existing bill
// ---------------------------------------------------------------------------

export interface BillConsequence {
  /** Who owes it, in words: "Ada Obi", "JSS 1A". */
  billedTo: string;
  /** One payer (a student or an applicant) rather than a whole class/section/programme. */
  single: boolean;
  /** Students sharing a group bill; 0/undefined when not known yet. */
  studentCount?: number | null;
  /** What each payer owes, before their own discounts. */
  amountEach: number;
}

type BillLike = Pick<Bill, 'billable_type' | 'net_amount'> & {
  aggregates?: Partial<Pick<BillAggregates, 'student_count'>> | null;
};

const SINGLE_PAYER_TYPES = new Set(['student', 'form_submission', 'transcript_request']);

/** Builds the consequence summary for a bill row/detail. */
export function billConsequence(bill: BillLike, billedTo: string): BillConsequence {
  return {
    billedTo,
    single: SINGLE_PAYER_TYPES.has(normalizeCode(bill.billable_type)),
    studentCount: bill.aggregates?.student_count ?? null,
    amountEach: Number(bill.net_amount) || 0,
  };
}

function groupAudience(c: BillConsequence): string {
  return c.studentCount && c.studentCount > 0
    ? `${pluralize(c.studentCount, 'student')} in ${c.billedTo}`
    : `every student in ${c.billedTo}`;
}

function groupTotal(c: BillConsequence): string {
  return c.studentCount && c.studentCount > 0
    ? ` (${formatCurrency(c.amountEach * c.studentCount)} in total)`
    : '';
}

const NO_CHANGES_AFTER_PUBLISH = 'You can\'t change the fees after publishing.';

export function publishBillConfirmMessage(c: BillConsequence): string {
  const amount = formatCurrency(c.amountEach);
  if (c.single) {
    return `Publish this bill to ${c.billedTo}? They will owe ${amount} and can start paying straight away. ${NO_CHANGES_AFTER_PUBLISH}`;
  }
  return `Publish this bill to ${groupAudience(c)}? Each will owe ${amount}${groupTotal(c)} and parents can start paying straight away. ${NO_CHANGES_AFTER_PUBLISH}`;
}

export function cancelBillConfirmMessage(c: BillConsequence): string {
  const amount = formatCurrency(c.amountEach);
  if (c.single) {
    return `Cancel this bill for ${c.billedTo}? They will no longer owe ${amount} and it will be removed from their balance. This can't be undone.`;
  }
  const who = c.studentCount && c.studentCount > 0
    ? `${pluralize(c.studentCount, 'student')} will no longer owe`
    : 'Students in this group will no longer owe';
  return `Cancel this bill for ${c.billedTo}? ${who} ${amount} each${groupTotal(c)} and it will be removed from their balances. This can't be undone.`;
}

export interface ToastCopy {
  summary: string;
  detail: string;
}

export function billPublishedToast(c: BillConsequence): ToastCopy {
  const amount = formatCurrency(c.amountEach);
  return {
    summary: 'Bill published',
    detail: c.single
      ? `${c.billedTo} can now see and pay ${amount}.`
      : `${groupAudience(c).replace(/^every/, 'Every')} can now see and pay ${amount} each.`,
  };
}

export function billCancelledToast(c: BillConsequence): ToastCopy {
  const amount = formatCurrency(c.amountEach);
  return {
    summary: 'Bill cancelled',
    detail: c.single
      ? `${c.billedTo} no longer owes ${amount}.`
      : `Students in ${c.billedTo} no longer owe ${amount} each.`,
  };
}

// ---------------------------------------------------------------------------
// Create-bill wizard
// ---------------------------------------------------------------------------

export interface NewBillSummary {
  billableType: string;
  /** Names of the selected students / classes / sections / programmes. */
  targetNames: string[];
  /** Sum of the fees — what each student owes. */
  amountEach: number;
  /** A Programme bill limited to one class charges only that class. */
  narrowedTo?: string;
}

function newBillAudience(s: NewBillSummary): string {
  if (s.billableType.toLowerCase() === 'student') {
    return s.targetNames.length === 1
      ? s.targetNames[0]!
      : `${pluralize(s.targetNames.length, 'student')} (${listNames(s.targetNames)})`;
  }
  const groups = listNames(s.targetNames) || 'the selected groups';
  return s.narrowedTo
    ? `every student in ${groups} who is in ${s.narrowedTo}`
    : `every student in ${groups}`;
}

export function newBillPublishConfirmMessage(s: NewBillSummary): string {
  const amount = formatCurrency(s.amountEach);
  const isOneStudent = s.billableType.toLowerCase() === 'student' && s.targetNames.length === 1;
  const owes = isOneStudent ? `They will owe ${amount}` : `Each student will owe ${amount}`;
  return `Publish this bill to ${newBillAudience(s)}? ${owes} and parents can start paying straight away. ${NO_CHANGES_AFTER_PUBLISH}`;
}

export function billCreatedToast(status: 'draft' | 'published', s: NewBillSummary): ToastCopy {
  const amount = formatCurrency(s.amountEach);
  const groups = listNames(s.targetNames) || targetCount(s.billableType, s.targetNames.length);
  const who = s.narrowedTo ? `${groups} (${s.narrowedTo} only)` : groups;
  if (status === 'draft') {
    return {
      summary: 'Draft saved',
      detail: `${amount} per student for ${who}. Parents can't see it until you publish it.`,
    };
  }
  return {
    summary: 'Bill published',
    detail: `${who} can now see and pay ${amount}${s.targetNames.length > 1 || s.billableType.toLowerCase() !== 'student' ? ' per student' : ''}.`,
  };
}

// ---------------------------------------------------------------------------
// Students on a bill
// ---------------------------------------------------------------------------

const STUDENT_PAYMENT_STATUS: Record<string, string> = {
  paid: 'Paid',
  partially_paid: 'Part paid',
  unpaid: 'Not paid',
};

/** A student's position on a bill (billStudentsWithStatus.payment_status). */
export function studentPaymentStatusLabel(status: string | null | undefined): string {
  const code = normalizeCode(status);
  return STUDENT_PAYMENT_STATUS[code] ?? (code ? code.charAt(0).toUpperCase() + code.slice(1).replace(/_/g, ' ') : '');
}

export function studentPaymentStatusSeverity(status: string | null | undefined): 'success' | 'warn' | 'danger' | 'info' {
  switch (normalizeCode(status)) {
  case 'paid': return 'success';
  case 'partially_paid': return 'warn';
  case 'unpaid': return 'danger';
  default: return 'info';
  }
}

// ---------------------------------------------------------------------------
// Server validation messages
// ---------------------------------------------------------------------------

const FIELD_WORDS: Record<string, string> = {
  bill_item_template_id: 'fee item',
  bill_item_category_id: 'category',
  item_name: 'fee name',
  default_amount: 'amount',
  billable_ids: 'selection',
  billable_type: 'bill type',
  session_id: 'session',
  academic_calendar_id: 'term',
  due_date: 'due date',
  gateway_settlement_account_id: 'bank account',
};

/**
 * Laravel names fields by their input path ("The input.items.0.amount field
 * must be at least 0.01."). Replace the path with a word a person would use.
 */
export function cleanValidationMessage(message: string): string {
  const fieldWord = (name: string) => FIELD_WORDS[name] ?? name.replace(/_/g, ' ');

  return message
    // Dotted input paths: input.items.0.amount, input.name
    .replace(/\b[a-z_]+(?:\.(?:\d+|[a-z_]+))+\b/g, (path) => {
      const looksLikePath = path.startsWith('input.') || /\.\d+(\.|$)/.test(path) || path.includes('_');
      if (!looksLikePath) return path;
      const last = path.split('.').filter((segment) => !/^\d+$/.test(segment)).pop() ?? path;
      return fieldWord(last);
    })
    // Bare snake_case field names: default_amount, session_id
    .replace(/\b[a-z]+(?:_[a-z]+)+\b/g, fieldWord);
}

/**
 * Server validation keys that point at a fee row ("input.items.2.amount")
 * mapped to that row, so the message can sit next to the fee it is about.
 */
export function itemErrorsFromServer(serverErrors: Record<string, string[]>): Record<number, string> {
  const rows: Record<number, string> = {};
  for (const [key, messages] of Object.entries(serverErrors)) {
    const match = /(?:^|\.)items\.(\d+)\./.exec(key);
    if (!match || !messages?.[0]) continue;
    const index = Number(match[1]);
    if (rows[index] === undefined) rows[index] = cleanValidationMessage(messages[0]);
  }
  return rows;
}
